import { type MediaState, PROCESSING_STAGE_LABELS_PL, type ProcessingStage } from "@anisync/types";
import { CheckIcon, ChevronRightIcon, DownloadIcon } from "lucide-react";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import type { PlayStatus } from "../lib/api";
import { formatDuration, formatSpeed } from "../lib/format";
import { cn } from "../lib/utils";

/**
 * Pełnoekranowy stan przygotowywania materiału.
 *
 * Jest pełnoprawnym widokiem, nie komunikatem, bo przy pierwszym uruchomieniu
 * odcinka użytkownik zobaczy go na kilkadziesiąt sekund albo dłużej. Musi więc
 * odpowiadać na trzy pytania bez pytania o nie: co się dzieje, jak długo to
 * jeszcze potrwa i skąd bierzemy materiał.
 */

/**
 * Kolejność etapów w pasku — odpowiada kolejności w potoku serwera.
 *
 * „Szukanie kopii" jest pierwsze od 12.10.2026: z Shinden trwa do pół minuty
 * (bramka tempa), a bez nazwy ekran stał na „Pobieram materiał" z pustym paskiem,
 * czyli wyglądał na zacięty, zanim cokolwiek zaczęło się pobierać.
 */
const STAGES: ProcessingStage[] = ["resolve", "download", "probe", "remux", "subs"];

const STATE_HEADINGS: Record<MediaState, string> = {
  queued: "W kolejce",
  acquiring: "Pobieram materiał",
  probing: "Sprawdzam plik",
  processing: "Przygotowuję do odtwarzania",
  ready: "Gotowe",
  failed: "Nie udało się",
};

export function DownloadState({ status }: { status: PlayStatus }) {
  const resolving = isResolving(status);
  const heading = resolving ? "Szukam kopii w źródłach" : STATE_HEADINGS[status.state];
  const percent = percentOf(status);

  return (
    <div className="flex min-h-[24rem] flex-col justify-center gap-6 rounded-xl border border-border bg-card p-8">
      <div className="flex flex-col gap-2">
        <p className="flex items-center gap-2 text-sm font-medium tracking-[0.2em] text-muted-foreground uppercase">
          <DownloadIcon className="size-4" />
          {resolving
            ? "Wyszukiwanie"
            : status.state === "acquiring"
              ? "Pobieranie"
              : "Przygotowanie"}
        </p>
        <h2 className="text-2xl font-bold">{heading}</h2>
        {resolving && (
          <p className="max-w-2xl text-muted-foreground">
            Pytam źródła o kopie tego odcinka i wybieram najlepszą — z polskimi napisami przed
            innymi. Część serwisów wymaga kilkunastu sekund odstępu między zapytaniami, więc ten
            krok trwa do pół minuty.
          </p>
        )}
        {status.attempt !== undefined && (
          <p className="flex flex-wrap items-center gap-2 text-muted-foreground">
            Źródło:
            <Badge variant="accent">{status.attempt.host}</Badge>
            {status.attempt.total > 1 && (
              <span className="text-sm">
                próba {status.attempt.index} z {status.attempt.total}
              </span>
            )}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {/*
          `tabIndex`, bo operacja trwa minuty: czytnik ekranu powinien móc odczytać
          stan na żądanie, nie tylko w chwili jego zmiany. `null` w wartości znaczy
          „host nie podał rozmiaru" — primityw pokazuje wtedy ruch bez procentu,
          zamiast zmyślać liczbę.
        */}
        <Progress
          value={percent}
          tabIndex={0}
          aria-label={`Postęp przygotowania materiału${percent !== null ? `: ${percent}%` : ""}`}
          className="focus-ring-own outline-none focus-visible:ring-[0.1875rem] focus-visible:ring-ring/50"
        />

        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm">
          {percent !== null && <span className="font-semibold tabular-nums">{percent}%</span>}
          {status.bytesDone !== undefined && (
            <span className="text-muted-foreground tabular-nums">
              {formatBytes(status.bytesDone)}
              {status.bytesTotal != null && ` z ${formatBytes(status.bytesTotal)}`}
            </span>
          )}
          {status.speedBps !== undefined && status.speedBps > 0 && (
            <span className="text-muted-foreground tabular-nums">
              {formatSpeed(status.speedBps)}
            </span>
          )}
          {status.etaSec !== undefined && status.etaSec > 0 && (
            <span className="text-muted-foreground">zostało {formatDuration(status.etaSec)}</span>
          )}
        </div>
      </div>

      <ol className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {STAGES.map((stage, index) => {
          const state = stageState(stage, status);
          return (
            <li key={stage} className="flex items-center gap-3">
              <span
                className={cn(
                  "flex items-center gap-1.5",
                  state === "done" && "text-success",
                  state === "active" && "font-semibold text-foreground",
                  state === "pending" && "text-muted-foreground/60",
                )}
              >
                {state === "done" ? (
                  <CheckIcon className="size-3.5" />
                ) : (
                  <span
                    className={cn(
                      "size-1.5 rounded-full",
                      state === "active" ? "animate-pulse bg-primary" : "bg-muted-foreground/40",
                    )}
                  />
                )}
                {PROCESSING_STAGE_LABELS_PL[stage]}
              </span>
              {index < STAGES.length - 1 && (
                <ChevronRightIcon className="size-3.5 text-muted-foreground/50" />
              )}
            </li>
          );
        })}
      </ol>

      <p className="text-sm text-muted-foreground">
        Materiał pobieramy na serwer, żeby wszyscy w pokoju widzieli tę samą klatkę, a napisy i
        przewijanie działały. Kolejne uruchomienie tego odcinka startuje natychmiast.
      </p>
    </div>
  );
}

/**
 * Czy trwa szukanie kopii. Także bez znanego etapu: materiał `acquiring` bez
 * postępu to chwila przed pierwszą odpowiedzią źródeł — a zaraz po kliknięciu
 * stan przychodzi z bazy, zanim proces zdąży cokolwiek ogłosić.
 */
function isResolving(status: PlayStatus): boolean {
  if (status.state !== "acquiring") return false;
  return (
    status.stage === "resolve" || (status.stage === undefined && status.bytesDone === undefined)
  );
}

/** Który etap jest w toku, a które są już za nami. */
function stageState(stage: ProcessingStage, status: PlayStatus): "done" | "active" | "pending" {
  const currentIndex = isResolving(status)
    ? 0
    : status.stage !== undefined
      ? STAGES.indexOf(status.stage)
      : -1;
  const myIndex = STAGES.indexOf(stage);

  if (status.state === "ready") return "done";
  if (currentIndex === -1) return stage === "download" ? "active" : "pending";
  if (myIndex < currentIndex) return "done";
  if (myIndex === currentIndex) return "active";
  return "pending";
}

function percentOf(status: PlayStatus): number | null {
  if (status.state === "ready") return 100;
  if (status.stagePercent !== undefined) return Math.round(status.stagePercent);
  if (status.bytesDone !== undefined && status.bytesTotal != null && status.bytesTotal > 0) {
    return Math.round((status.bytesDone / status.bytesTotal) * 100);
  }
  return null;
}

function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GiB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MiB`;
  return `${(bytes / 1024).toFixed(0)} KiB`;
}
